import React from 'react';
import { MonitorIcon, MoonIcon, SettingsIcon, SunIcon } from 'lucide-react';
import type { LifeProfile, LifeStats, ViewMode } from '../types/life';
import type { ThemePreference, ThemeState } from '../hooks/useTheme';
import { cn } from '../utils/cn';
import { formatNumber, plural } from '../utils/format';
interface TopBarProps {
  view: ViewMode;
  onViewChange: (view: ViewMode) => void;
  stats: LifeStats;
  profile: LifeProfile;
  theme: ThemeState;
  onOpenSettings: () => void;
}
const TABS: Array<{
  id: ViewMode;
  label: string;
}> = [{
  id: 'now',
  label: 'Now'
}, {
  id: 'life',
  label: 'Whole life'
}];
const NEXT_THEME: Record<ThemePreference, ThemePreference> = {
  system: 'light',
  light: 'dark',
  dark: 'system'
};
const THEME_LABEL: Record<ThemePreference, string> = {
  system: 'system',
  light: 'light',
  dark: 'dark'
};
export function TopBar({
  view,
  onViewChange,
  stats,
  profile,
  theme,
  onOpenSettings
}: TopBarProps) {
  const ThemeIcon = theme.preference === 'system' ? MonitorIcon : theme.preference === 'light' ? SunIcon : MoonIcon;
  return <header className="border-line bg-paper flex shrink-0 flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b px-4 py-3 sm:px-6 sm:py-4" data-id="element-129" data-mp="27lpmcoo8fmw">
      <div className="flex items-center gap-4 sm:gap-8" data-id="element-130" data-mp="3ok0yxo40gpri">
        <span className="text-ink hidden font-mono text-xs font-medium sm:inline" data-id="element-131" data-mp="3tjncjynr480u">not.rip</span>
        <nav aria-label="Scale" className="flex items-center gap-1" data-id="element-132" data-mp="3emmk1sxqk91b">
          {TABS.map(tab => {
          const active = tab.id === view;
          return <button key={tab.id} type="button" aria-current={active ? 'page' : undefined} onClick={() => onViewChange(tab.id)} className={cn('rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-150 ease-swift', active ? 'bg-ink text-paper' : 'text-muted hover:text-ink')} data-id="element-133" data-mp="3jn0u1giwdt1z">
                {tab.label}
              </button>;
        })}
        </nav>
      </div>

      <div className="flex items-center gap-3 sm:gap-5" data-id="element-134" data-mp="12cwhsg9w7ee5">
        <p className="text-muted text-[11px] sm:hidden" data-id="element-135" data-mp="12fimm85effso">
          <span className="text-accent font-mono tabular-nums" data-id="element-136" data-mp="xfe4kdoued0c">{formatNumber(stats.weeksLeft)}</span> weeks left
          <span className="mx-1.5" data-id="element-137" data-mp="sgjmxf2m2uht">·</span>
          <span className="font-mono tabular-nums" data-id="element-138" data-mp="shkx7hbszcyh">{stats.percentLived.toFixed(0)}%</span>
        </p>
        <p className="text-muted hidden text-xs sm:block" data-id="element-139" data-mp="1l3xd8ubt0rsy">
          <span className="text-ink font-mono tabular-nums" data-id="element-140" data-mp="1l4fiuhkcwidj">{formatNumber(stats.weeksLived)}</span>{' '}
          {plural(stats.weeksLived, 'week')} lived
          <span className="mx-2" data-id="element-141" data-mp="1bedqveovuftc">·</span>
          <span className="text-accent font-mono tabular-nums" data-id="element-142" data-mp="1bdb0d7vh2fdy">{formatNumber(stats.weeksLeft)}</span> left
          <span className="mx-2" data-id="element-143" data-mp="28v2rj523jc15">·</span>
          <span className="font-mono tabular-nums" data-id="element-144" data-mp="242rpnwnspg5y">{stats.percentLived.toFixed(1)}%</span>
        </p>
        <div className="flex items-center gap-1" data-id="element-145" data-mp="2be65r854j63d">
          <button type="button" onClick={() => theme.setPreference(NEXT_THEME[theme.preference])} className="text-muted hover:text-ink p-1 transition-colors duration-150 ease-swift" title={`Theme: ${THEME_LABEL[theme.preference]}`} aria-label={`Theme: ${THEME_LABEL[theme.preference]}. Switch to ${THEME_LABEL[NEXT_THEME[theme.preference]]}`} data-id="element-146" data-mp="2bfh8d4mu27sw">
            <ThemeIcon className="h-4 w-4" data-id="element-147" data-mp="2uciit4z5p048" />
          </button>
          <button type="button" onClick={onOpenSettings} className="text-muted hover:text-ink p-1 transition-colors duration-150 ease-swift" aria-label={`Settings: born ${profile.birthDate}, horizon ${profile.lifeExpectancy} years`} data-id="element-148" data-mp="2zatqb91zayja">
            <SettingsIcon className="h-4 w-4" data-id="element-149" data-mp="3e1kkuouygbq3" />
          </button>
        </div>
      </div>
    </header>;
}